import React, { useState, useEffect } from 'react';
import { X, Loader2, Calendar, Lock } from 'lucide-react';
import { notifyError, notifySuccess } from '../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';

const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25 transition';
const labelCls =
  'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';

// iter 142c - shared city dropdown options for both events and Mundalo's other
// public city surfaces. Matches the FALLBACK_CITIES list on PortalEvents.
const CITY_OPTIONS = [
  'Mexico City', 'Tulum', 'Puerto Vallarta', 'Oaxaca',
  'San Miguel de Allende', 'Mérida', 'Guadalajara', 'Cabo San Lucas',
  'Monterrey', 'Playa del Carmen',
];

/**
 * Member-facing event submission form. Submissions land in
 * db.event_submissions with status="pending" and require admin approval
 * via /admin/events to surface publicly.
 *
 * iter 142 - when `adminMode` is true, the form POSTs to
 * /api/admin/events/create instead, which inserts directly with
 * status="approved" so the event goes live immediately.
 *
 * iter 142c - adds two member-only fields:
 *   • `city` - dropdown of Mundalo's supported cities
 *   • Optional "Limit to my Peer Circle" toggle + group selector. Only
 *     rendered when the viewer actually has memberships (community / connected
 *     tiers; free-trial users see no toggle since they can't be in circles).
 *     adminMode never shows the peer-circle UI - admin posts are platform-wide.
 */
const SubmitEventModal = ({ open, onClose, onSubmitted, adminMode = false }) => {
  const [form, setForm] = useState({
    title: '',
    summary: '',
    description: '',
    category: 'Community',
    type: 'In-Person',
    location: '',
    when_local: '',
    duration_minutes: 90,
    url: '',
    city: '',
    peer_circle_group_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [myGroups, setMyGroups] = useState([]);
  const [limitToCircle, setLimitToCircle] = useState(false);

  // Fetch the viewer's peer-circle memberships once the modal opens.
  // adminMode never fetches - admin direct-creates are always public.
  useEffect(() => {
    if (!open || adminMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/peer-circles/groups`, {
          credentials: 'include',
        });
        if (!r.ok) {
          // 401 (logged out) or 403 (trial-tier gate) - silently skip the toggle.
          return;
        }
        const d = await r.json();
        const mine = (d.groups || []).filter((g) => g.is_member);
        if (!cancelled) setMyGroups(mine);
      } catch {
        /* network - no toggle, fine */
      }
    })();
    return () => { cancelled = true; };
  }, [open, adminMode]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.when_local) {
      notifyError('Please pick a date and time.');
      return;
    }
    // iter 142c - peer-circle scope only sends when the toggle is on AND
    // the user picked a circle. Otherwise we send null and the event is
    // public-to-all upon approval.
    const scopedGroupId =
      !adminMode && limitToCircle && form.peer_circle_group_id
        ? form.peer_circle_group_id
        : null;

    setSubmitting(true);
    try {
      const endpoint = adminMode ? '/api/admin/events/create' : '/api/events/submit';
      const r = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          summary: form.summary,
          description: form.description,
          category: form.category,
          type: form.type,
          location: form.location,
          when_utc: new Date(form.when_local).toISOString(),
          duration_minutes: parseInt(form.duration_minutes, 10) || 90,
          url: form.url || undefined,
          city: form.city || undefined,
          peer_circle_group_id: scopedGroupId || undefined,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) {
        notifySuccess(
          adminMode
            ? 'Event published - it’s live on /events now.'
            : 'Your event has been submitted for review. We’ll notify you once it’s approved.'
        );
        onSubmitted?.();
        onClose();
      } else if (r.status === 401) {
        notifyError(adminMode ? 'Admin session expired - log in again.' : 'Please log in to submit an event.');
      } else {
        notifyError(d, 'Could not submit your event. Please try again.');
      }
    } catch {
      notifyError('Network error - please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="submit-event-modal"
      onClick={onClose}
    >
      <div
        className="relative bg-[#F9F6EF] w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-[#F9F6EF] border-b border-[#EFE6CF] flex items-center justify-between p-5">
          <div>
            <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1">
              {adminMode ? 'Create curated event' : 'Submit an event'}
            </p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E]">
              {adminMode ? 'Publish directly' : 'Tell the community'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#0A1F2E]/40 hover:text-[#0A1F2E] transition"
            data-testid="submit-event-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <form onSubmit={submit} className="p-5 space-y-4" data-testid="submit-event-form">
          <div>
            <label className={labelCls}>Title</label>
            <input
              type="text"
              required minLength={4} maxLength={140}
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              className={inputCls}
              placeholder="Newcomers Coffee Hour, Roma Norte"
              data-testid="submit-event-title"
            />
          </div>

          <div>
            <label className={labelCls}>One-line summary</label>
            <input
              type="text"
              required minLength={10} maxLength={240}
              value={form.summary}
              onChange={(e) => set('summary', e.target.value)}
              className={inputCls}
              placeholder="A weekly coffee meetup for new movers in Roma Norte."
              data-testid="submit-event-summary"
            />
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              required minLength={20} maxLength={2000}
              rows={4}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className={`${inputCls} resize-none`}
              placeholder="What it is, who it's for, what to bring, how to find the host."
              data-testid="submit-event-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Category</label>
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className={inputCls}
                data-testid="submit-event-category"
              >
                {['Community', 'Banking', 'Healthcare', 'Visas', 'Housing', 'Family', 'Career', 'Other'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Format</label>
              <select
                value={form.type}
                onChange={(e) => set('type', e.target.value)}
                className={inputCls}
                data-testid="submit-event-type"
              >
                <option value="In-Person">In-Person</option>
                <option value="Virtual">Virtual</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Where (venue / address, or “Zoom”)</label>
            <input
              type="text"
              required minLength={2} maxLength={180}
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              className={inputCls}
              placeholder='e.g. "Cafe Avellaneda, Roma Norte" or "Zoom"'
              data-testid="submit-event-location"
            />
          </div>

          <div>
            <label className={labelCls}>City</label>
            <select
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              className={inputCls}
              data-testid="submit-event-city"
              required={!adminMode}
            >
              <option value="">Select a city…</option>
              {CITY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="Other">Other / Not listed</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>When</label>
              <input
                type="datetime-local"
                required
                value={form.when_local}
                onChange={(e) => set('when_local', e.target.value)}
                className={inputCls}
                data-testid="submit-event-when"
              />
            </div>
            <div>
              <label className={labelCls}>Duration (minutes)</label>
              <input
                type="number"
                required min={15} max={600}
                value={form.duration_minutes}
                onChange={(e) => set('duration_minutes', e.target.value)}
                className={inputCls}
                data-testid="submit-event-duration"
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Link (optional - Zoom URL, RSVP page, etc.)</label>
            <input
              type="url"
              maxLength={500}
              value={form.url}
              onChange={(e) => set('url', e.target.value)}
              className={inputCls}
              placeholder="https://"
              data-testid="submit-event-url"
            />
          </div>

          {/* iter 142c - Peer Circle scope. Only render the toggle when the
              viewer actually has memberships (community/connected tiers).
              adminMode never sees this (admin posts are platform-wide). */}
          {!adminMode && myGroups.length > 0 && (
            <div className="bg-white border border-[#EFE6CF] rounded-lg p-4 space-y-3" data-testid="submit-event-pc-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={limitToCircle}
                  onChange={(e) => {
                    setLimitToCircle(e.target.checked);
                    if (!e.target.checked) set('peer_circle_group_id', '');
                  }}
                  className="mt-1 w-4 h-4 accent-[#2CA8A0]"
                  data-testid="submit-event-pc-toggle"
                />
                <div className="flex-1">
                  <span className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]">
                    <Lock className="w-3 h-3 text-[#1A5C6E]" />
                    Limit to my Peer Circle
                  </span>
                  <p className="text-xs text-[#0A1F2E]/65 mt-1">
                    Only members of the selected circle will see this event after approval.
                  </p>
                </div>
              </label>
              {limitToCircle && (
                <select
                  value={form.peer_circle_group_id}
                  onChange={(e) => set('peer_circle_group_id', e.target.value)}
                  className={inputCls}
                  data-testid="submit-event-pc-select"
                  required
                >
                  <option value="">Select a circle…</option>
                  {myGroups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name} · {g.city}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-lg p-3 text-xs text-[#0A1F2E]/75">
            <Calendar className="inline w-3.5 h-3.5 mr-1 text-[#E8A830] -mt-0.5" />
            {adminMode
              ? 'Curated events publish instantly to /events with the “Mundalo Team” byline.'
              : 'Submissions are reviewed by the Mundalo team - usually within 24 hours.'}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm text-[#0A1F2E]/70 hover:text-[#0A1F2E] font-mono uppercase tracking-[0.12em] text-[11px]"
              data-testid="submit-event-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="submit-event-submit"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> {adminMode ? 'Publishing…' : 'Submitting…'}</>
                : (adminMode ? 'Publish event' : 'Submit for review')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitEventModal;
